import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  CheckSquare,
  FolderKanban,
  MoreHorizontal,
  X,
  Users,
  Briefcase,
  UserCheck,
  DollarSign,
  BarChart3,
  Clock,
  MessageSquare,
  Globe,
  Settings,
  LogOut,
  ChevronRight,
  Activity,
  Megaphone,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext.js';

interface BottomTabBarProps {
  onOpenQuickAdd?: () => void;
}

export function BottomTabBar(_props: BottomTabBarProps = {}) {
  const [showMore, setShowMore] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const currentPath = location.pathname;
  const role = user?.role || 'team_member';
  const isOwner = role === 'owner';
  const isManager = role === 'manager';
  const isViewer = role === 'viewer';

  const isTabActive = (path: string) => {
    if (path === '/dashboard') return currentPath === '/dashboard';
    if (path === '/tasks') return currentPath.startsWith('/tasks') || currentPath.startsWith('/my-tasks');
    if (path === '/projects') return currentPath.startsWith('/projects');
    return false;
  };

  const navTo = (path: string) => {
    navigate(path);
    setShowMore(false);
  };

  // Sections inside the "More" Drawer based on RBAC matrix
  const moreSections = [
    // Clients, Leads & Promotions (Owner / Manager ONLY)
    ...((isOwner || isManager) ? [
      { name: 'Clients', path: '/clients', icon: Briefcase, desc: 'Client accounts & CRM' },
      { name: 'Leads', path: '/leads', icon: UserCheck, desc: 'Pipeline & conversions' },
      { name: 'Promotions', path: '/promotions', icon: Megaphone, desc: 'Holiday sales & campaigns' },
    ] : []),
    // Team Directory (Non-viewers)
    ...(!isViewer ? [
      { name: 'Team Directory', path: '/team', icon: Users, desc: 'Members & workload' },
    ] : []),
    // Finance (Owner ONLY)
    ...(isOwner ? [
      { name: 'Finance & Invoices', path: '/finance', icon: DollarSign, desc: 'Agency revenue & margins' },
    ] : []),
    // Reports & Activity Logs (All roles)
    { name: 'Reports & Analytics', path: '/reports', icon: BarChart3, desc: 'Workload & on-time stats' },
    { name: 'Activity & Audit Logs', path: '/activity', icon: Activity, desc: 'Compliance & audit trail' },
    // Time Tracking (Non-viewers)
    ...(!isViewer ? [
      { name: 'Time Tracking', path: '/time', icon: Clock, desc: 'Timesheets & timers' },
      { name: 'Daily Standups', path: '/standups', icon: MessageSquare, desc: 'Async agency updates' },
      { name: 'SEO Audits', path: '/seo', icon: Globe, desc: 'Technical SEO health' },
    ] : []),
    // Settings & API Keys (Owner ONLY)
    ...(isOwner ? [
      { name: 'Settings & API Keys', path: '/settings', icon: Settings, desc: 'Workspace config & keys' },
    ] : []),
  ];

  return (
    <>
      {/* Bottom Tab Bar Container */}
      <nav
        aria-label="Mobile Navigation"
        className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-[#12211F]/95 backdrop-blur-md border-t border-brand-border md:hidden px-3 pt-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom,0.5rem))] shadow-lg"
      >
        <div className="flex items-center justify-around max-w-md mx-auto">
          {/* Tab 1: Dashboard */}
          <button
            onClick={() => navTo('/dashboard')}
            className={`flex flex-col items-center justify-center min-w-[56px] min-h-[46px] py-1 px-2 rounded-lg transition-colors ${
              isTabActive('/dashboard')
                ? 'text-brand font-bold'
                : 'text-brand-muted hover:text-brand-ink'
            }`}
            aria-label="Dashboard"
          >
            <LayoutDashboard className="h-5 w-5 mb-0.5" />
            <span className="text-[11px] leading-tight tracking-tight">Dashboard</span>
            {isTabActive('/dashboard') && (
              <span className="h-1 w-1 rounded-full bg-brand mt-0.5" />
            )}
          </button>

          {/* Tab 2: Tasks */}
          <button
            onClick={() => navTo('/my-tasks')}
            className={`flex flex-col items-center justify-center min-w-[56px] min-h-[46px] py-1 px-2 rounded-lg transition-colors ${
              isTabActive('/tasks')
                ? 'text-brand font-bold'
                : 'text-brand-muted hover:text-brand-ink'
            }`}
            aria-label="Tasks"
          >
            <CheckSquare className="h-5 w-5 mb-0.5" />
            <span className="text-[11px] leading-tight tracking-tight">Tasks</span>
            {isTabActive('/tasks') && (
              <span className="h-1 w-1 rounded-full bg-brand mt-0.5" />
            )}
          </button>

          {/* Tab 3: Projects */}
          <button
            onClick={() => navTo('/projects')}
            className={`flex flex-col items-center justify-center min-w-[56px] min-h-[46px] py-1 px-2 rounded-lg transition-colors ${
              isTabActive('/projects')
                ? 'text-brand font-bold'
                : 'text-brand-muted hover:text-brand-ink'
            }`}
            aria-label="Projects"
          >
            <FolderKanban className="h-5 w-5 mb-0.5" />
            <span className="text-[11px] leading-tight tracking-tight">Projects</span>
            {isTabActive('/projects') && (
              <span className="h-1 w-1 rounded-full bg-brand mt-0.5" />
            )}
          </button>

          {/* Tab 4: More */}
          <button
            onClick={() => setShowMore(true)}
            className={`flex flex-col items-center justify-center min-w-[56px] min-h-[46px] py-1 px-2 rounded-lg transition-colors ${
              showMore || (!isTabActive('/dashboard') && !isTabActive('/tasks') && !isTabActive('/projects'))
                ? 'text-brand font-bold'
                : 'text-brand-muted hover:text-brand-ink'
            }`}
            aria-label="More sections"
          >
            <MoreHorizontal className="h-5 w-5 mb-0.5" />
            <span className="text-[11px] leading-tight tracking-tight">More</span>
            {(!isTabActive('/dashboard') && !isTabActive('/tasks') && !isTabActive('/projects')) && (
              <span className="h-1 w-1 rounded-full bg-brand mt-0.5" />
            )}
          </button>
        </div>
      </nav>

      {/* "More" Bottom Sheet Drawer */}
      {showMore && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm md:hidden flex flex-col justify-end transition-opacity"
          onClick={() => setShowMore(false)}
        >
          <div
            className="bg-white dark:bg-[#12211F] rounded-t-2xl max-h-[85vh] overflow-y-auto w-full p-4 pb-8 space-y-4 border-t border-brand-border shadow-2xl animate-in slide-in-from-bottom-5 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer Handle & Header */}
            <div className="flex items-center justify-between pb-3 border-b border-brand-border">
              <div className="flex items-center space-x-2">
                <div className="h-2 w-8 rounded-full bg-brand-border mx-auto -mt-2 absolute left-1/2 -translate-x-1/2 top-2" />
                <div className="h-8 w-8 rounded-lg bg-brand-teal text-white flex items-center justify-center font-bold text-xs">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-brand-ink">{user?.name || 'User'}</h3>
                  <span className="text-[10px] text-brand-muted capitalize font-medium">
                    {user?.role?.replace('_', ' ')} • Techosolution
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowMore(false)}
                className="p-2 text-brand-muted hover:text-brand-ink min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Navigation Grid */}
            <div className="grid grid-cols-1 gap-1.5">
              {moreSections.map((sec) => {
                const Icon = sec.icon;
                const active = currentPath.startsWith(sec.path);
                return (
                  <button
                    key={sec.path}
                    onClick={() => navTo(sec.path)}
                    className={`flex items-center justify-between p-3 rounded-xl transition-all min-h-[48px] text-left ${
                      active
                        ? 'bg-brand-tint/50 text-brand font-semibold border border-brand/20'
                        : 'text-brand-ink hover:bg-slate-50 dark:hover:bg-slate-800/60'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${active ? 'bg-brand text-white' : 'bg-slate-100 dark:bg-slate-800 text-brand-muted'}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="text-xs font-semibold">{sec.name}</div>
                        <div className="text-[10px] text-brand-muted">{sec.desc}</div>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-brand-muted" />
                  </button>
                );
              })}
            </div>

            {/* Logout button */}
            <div className="pt-2 border-t border-brand-border">
              <button
                onClick={() => {
                  setShowMore(false);
                  logout();
                }}
                className="w-full flex items-center justify-center space-x-2 py-3 rounded-xl text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 text-xs font-semibold min-h-[44px]"
              >
                <LogOut className="h-4 w-4" />
                <span>Sign Out of Project OS</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default BottomTabBar;
